"use client";

import { useId, useRef, useState } from "react";
import Link from "next/link";
import Script from "next/script";
import { CheckCircleIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { TrackingEvents } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";
import {
  buildBrowserAttributionEnvelope,
  createSubmissionId,
  setBrowserAttributionLeadId,
} from "@/lib/ad-attribution";
import { useTurnstile } from "@/hooks/useTurnstile";
import { submitCrmForm } from "@/lib/form-submission";
import { FieldLabel, InlineError, fieldClassName } from "@/components/shared/DesignSystem";

const INTENTS = [
  ["", "Select what you need"],
  ["product-selection", "Help choosing a new system"],
  ["existing-system-service", "Service for an existing system"],
  ["warranty-support", "Warranty or product support"],
  ["business-project", "Commercial or industrial project"],
  ["showroom-visit", "Plan a showroom visit"],
  ["general", "Another question"],
];

const INITIAL_DATA = {
  intent: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  note: "",
};

export function ContactForm() {
  const id = useId();
  const formRef = useRef(null);
  const submissionIdRef = useRef(null);
  const submittingRef = useRef(false);
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { turnstileRef, turnstileToken, resetTurnstile, handleScriptLoad } = useTurnstile();
  const trackingDimensions = { form_type: "contact", form_variant: "contact-page-general", placement: "inline", cta_variant: "contact-page", page_path: "/contact" };
  const trackStart = useFormTracking({ formRef, dimensions: trackingDimensions });

  const turnstileError = turnstileToken ? null : errors.turnstile;

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  }

  function validate() {
    const nextErrors = {};
    if (!formData.intent) nextErrors.intent = "Select what you need help with.";
    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      nextErrors.phone = "Enter a valid phone number with 10 to 15 digits.";
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!turnstileToken) nextErrors.turnstile = "Complete the human verification check.";
    return nextErrors;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    trackStart();

    const nextErrors = validate();
    TrackingEvents.trackFormStep("submit_attempt", { ...trackingDimensions, contact_intent: formData.intent || "unset" });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus({ type: "error", message: "Please check the highlighted fields and try again." });
      TrackingEvents.trackFormStep("validation_error", { ...trackingDimensions, error_fields: Object.keys(nextErrors).join(","), error_count: Object.keys(nextErrors).length });
      requestAnimationFrame(() => formRef.current?.querySelector("[aria-invalid='true']")?.focus());
      return;
    }

    submittingRef.current = true;
    try {
      setIsSubmitting(true);
      setStatus({ type: "idle", message: "" });
      submissionIdRef.current ||= createSubmissionId();
      const attribution = buildBrowserAttributionEnvelope({
        submissionId: submissionIdRef.current,
        formType: "contact",
      });
      const { response, result } = await submitCrmForm("/api/crm/submit-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "contact",
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          customer_note: formData.note,
          contact_intent: formData.intent,
          source: attribution?.submissionTouch?.source,
          attribution,
          consultation_context: {
            mode: formData.intent === "business-project" ? "business" : "general",
            audience: formData.intent === "business-project" ? "business" : "general",
            service: formData.intent === "business-project" ? "business-water" : "general",
            pagePath: "/contact",
            placement: "inline",
            ctaVariant: `contact-${formData.intent}`,
          },
          turnstile_token: turnstileToken,
        }),
      });
      if (!response.ok || !result.success) {
        if (result.fieldErrors) {
          setErrors((current) => ({
            ...current,
            ...(result.fieldErrors.first_name ? { firstName: result.fieldErrors.first_name } : {}),
            ...(result.fieldErrors.phone ? { phone: result.fieldErrors.phone } : {}),
            ...(result.fieldErrors.email ? { email: result.fieldErrors.email } : {}),
          }));
        }
        const submissionError = new Error(result.error || "Your request could not be submitted.");
        submissionError.code = result.code;
        throw submissionError;
      }

      const leadId = result.data?.leadId || result.data?.project?.projectId || result.data?.project?.id;
      if (!leadId) throw new Error("Lead creation was not confirmed.");

      try { setBrowserAttributionLeadId(leadId); }
      catch (storageError) { console.error("Confirmed lead storage failed:", storageError); }
      try {
        TrackingEvents.trackContactForm({ formId: leadId, ...trackingDimensions, contact_intent: formData.intent });
      } catch (trackingError) { console.error("Confirmed lead tracking failed:", trackingError); }
      submissionIdRef.current = null;
      setFormData(INITIAL_DATA);
      setErrors({});
      setStatus({
        type: "success",
        message: "We’ll call to confirm what you need and, when appropriate, arrange a visit. If relevant, keep your address, water test results, and current equipment details handy.",
      });
      resetTurnstile();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.code === "submission_unconfirmed"
          ? error.message
          : `${error instanceof Error ? error.message : "We could not submit your request."} Try again, or call 289-748-9666.`,
      });
      resetTurnstile();
      try {
        TrackingEvents.trackFormStep("submit_error", { ...trackingDimensions, error_type: "submission_failed" });
      } catch (trackingError) {
        console.error("Submission error tracking failed:", trackingError);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  if (status.type === "success") {
    return (
      <div className="rounded-[1.375rem] border border-emerald-200 bg-emerald-50 p-6 sm:p-8" role="status" aria-live="polite">
        <CheckCircleIcon className="h-9 w-9 text-water-teal" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-semibold text-water-ink">Request received</h3>
        <p className="mt-3 text-base leading-7 text-water-muted">{status.message}</p>
        <a href="tel:+12897489666" className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-water-blue hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2">
          <PhoneIcon className="h-5 w-5" aria-hidden="true" />
          Call 289-748-9666
        </a>
      </div>
    );
  }

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        async
        defer
        strategy="afterInteractive"
        onLoad={handleScriptLoad}
      />

      <form ref={formRef} onSubmit={handleSubmit} onFocusCapture={trackStart} className="wd-form space-y-5" noValidate>
        <div>
          <FieldLabel htmlFor={`${id}-intent`}>What do you need help with?</FieldLabel>
          <select
            id={`${id}-intent`}
            name="intent"
            value={formData.intent}
            onChange={handleChange}
            required
            aria-invalid={Boolean(errors.intent)}
            aria-describedby={errors.intent ? `${id}-intent-error` : undefined}
            className={fieldClassName}
          >
            {INTENTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <InlineError id={`${id}-intent-error`}>{errors.intent}</InlineError>
        </div>

        {formData.intent === "business-project" ? (
          <p className="rounded-[0.875rem] border border-water-border bg-water-canvas p-4 text-sm leading-6 text-water-muted">
            Have flow, process, or facility details ready? The <Link href="/forms/business-assessment" className="font-semibold text-water-blue underline underline-offset-4">business assessment</Link> gives our technical team more useful context.
          </p>
        ) : null}

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor={`${id}-first-name`}>First name</FieldLabel>
            <input id={`${id}-first-name`} name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? `${id}-first-name-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-first-name-error`}>{errors.firstName}</InlineError>
          </div>
          <div>
            <FieldLabel htmlFor={`${id}-last-name`} optional>Last name</FieldLabel>
            <input id={`${id}-last-name`} name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} className={fieldClassName} />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor={`${id}-phone`}>Phone</FieldLabel>
            <input id={`${id}-phone`} name="phone" type="tel" inputMode="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} placeholder="289-748-9666" required aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? `${id}-phone-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-phone-error`}>{errors.phone}</InlineError>
          </div>
          <div>
            <FieldLabel htmlFor={`${id}-email`} optional>Email</FieldLabel>
            <input id={`${id}-email`} name="email" type="email" inputMode="email" autoComplete="email" value={formData.email} onChange={handleChange} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? `${id}-email-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-email-error`}>{errors.email}</InlineError>
          </div>
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-note`} optional>Helpful details</FieldLabel>
          <textarea
            id={`${id}-note`}
            name="note"
            rows={4}
            value={formData.note}
            onChange={handleChange}
            placeholder={formData.intent === "warranty-support" ? "Product model, purchase date, and what is happening…" : "City, current system, water concern, or the best time to call…"}
            className={fieldClassName}
          />
        </div>

        <div>
          <div ref={turnstileRef} className="min-h-[66px]" aria-label="Human verification" />
          <InlineError id={`${id}-turnstile-error`}>{turnstileError}</InlineError>
        </div>

        {status.type === "error" ? (
          <p className="rounded-[0.875rem] border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800" role="alert">{status.message}</p>
        ) : null}

        <button type="submit" disabled={isSubmitting} className="inline-flex min-h-12 w-full items-center justify-center rounded-[0.875rem] border border-water-blue bg-water-blue px-6 py-3 text-base font-semibold text-white transition-[background-color,border-color] duration-200 hover:border-water-blue-dark hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 sm:w-auto">
          {isSubmitting ? "Sending request…" : "Send My Request"}
        </button>
        <p className="text-sm leading-6 text-water-muted">No obligation. We use these details only to respond and route your request.</p>
      </form>
    </>
  );
}
